import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from '@/hooks';
import { isArrayWithLength } from '../../helpers';
import { StyledMenuOptionWrapper } from './components/Menu/styled';
import { Input, Value, ToggleIcon, Menu, WindowedMenu, AsyncLoader } from './components';
import { StyledControlWrapper, StyledDropdownWrapper, StyledFormFeedback, StyledMenuWrapper, StyledValueWrapper } from './styled';
import { trimAndFormatStr, normalizeSelectedValue, getFocusOptionIndex, scrollMenuIntoViewOnOpen, parentNodeClasses, isObject } from './utils';

const Dropdown = (props) => {
  const [cancelBlur, setCancelBlur] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [menuOptions, setMenuOptions] = useState([]);
  const [focusedOption, setFocusedOption] = useState(null);
  const [menuOpen, toggleMenu] = useState(!!props.startOpen);
  const [isFocused, setIsFocused] = useState(!!props.startFocused);

  // debounced inputValue for async search only (0 by default and if so, will prevent effect from running and updating value beyond initial set)
  const debouncedInputValue = useDebounce(inputValue, props.searchDelay);

  // Element refs (inputRef, focusedOptionRef set in callback mapped to ref attribute on react node)
  const menuRef = useRef();
  const [inputRef, setInputRef] = useState(null);
  const [focusedOptionRef, setFocusedOptionRef] = useState(null);

  const isUsingFixedOptions = useMemo(() => {
    return !!(typeof(props.fixedOptions) === 'object' && props.fixedOptions.length)
  }, [props.fixedOptions]);

  const handleSetInputRef = useCallback((ref) => {
    if (!inputRef) {
      setInputRef(ref)
    }
  }, [inputRef]);

  const getOptionValue = (data) => {
    return (typeof props.getOptionValue === 'function')
      ? props.getOptionValue(data)
      : data.value;
  };

  const getOptionLabel = (data) => {
    return (typeof props.getOptionLabel === 'function')
      ? props.getOptionLabel(data)
      : data.label;
  };

  const getFixedOptionValue = (data) => {
    return (typeof props.getFixedOptionValue === 'function')
      ? props.getFixedOptionValue(data)
      : data.value;
  };

  const getFixedOptionLabel = (data) => {
    return (typeof props.getFixedOptionLabel === 'function')
      ? props.getFixedOptionLabel(data)
      : data.label;
  };
  
  const formatOptionLabel = useCallback((data) => {
    if (isUsingFixedOptions && props.fixedOptions.find(q => getFixedOptionValue(q) === getFixedOptionValue(data))) {
      return (typeof props.formatOptionLabel === 'function') 
        ? props.formatFixedOptionLabel(data) 
        : getFixedOptionLabel(data);
    }

    return (typeof props.formatOptionLabel === 'function') 
      ? props.formatOptionLabel(data)
      : getOptionLabel(data);
  }, []);

  const [selectedValue, setSelectedValue] = useState(() => 
    normalizeSelectedValue((props.value !== undefined) ? props.value : props.defaultValue, props.options, getOptionValue)
  );

  const focusedOptionKey = useMemo(() => {
    const isOptionFixed = (option) => {
      return !!(isUsingFixedOptions && props.fixedOptions.find(q => q.id === option.id));
    }

    return focusedOption
      ? isUsingFixedOptions && isOptionFixed(focusedOption)
        ? getFixedOptionValue(focusedOption)
        : getOptionValue(focusedOption)
      : null;
  }, [isUsingFixedOptions, focusedOption]);
  const focusedOptionIndex = getFocusOptionIndex(focusedOption);

  // TODO: share input value search state between the two - hacky way of getting input val for async comp via callback
  /* 
  useEffect(() => {
    if (typeof props.setAsyncSearchOptions === 'function') {
      props.setAsyncSearchOptions((prevAsyncSearchParams) => ({
        load: prevAsyncSearchParams.searchValue !== debouncedInputValue,
        searchValue: debouncedInputValue || '',
      }));
    }
  }, [debouncedInputValue]);
  */

  // TODO: figure out a way to break out this state between the standard and async components - just bailing out for each in the other
  // REGULARD DROPDOWN
  useEffect(() => {
    const { value, options, fixedOptions } = props;
    const fixedOptionCount = fixedOptions ? fixedOptions.length : 0;
    const normalizedValue = isUsingFixedOptions
      ? normalizeSelectedValue(value, fixedOptions, getFixedOptionValue)
      : normalizeSelectedValue(value, options, getOptionValue);

    const getMenuOptions = buildMenuOptions(options, fixedOptions, normalizedValue, debouncedInputValue || '');
    (getMenuOptions.length - fixedOptionCount === 1) && setFocusedOption({...getMenuOptions[fixedOptionCount].data, index: fixedOptionCount });
    setMenuOptions(getMenuOptions);
    setSelectedValue(normalizedValue);
  }, [props.value, props.options, debouncedInputValue]);

  // ASYNC DROPDOWN
  /*
  useEffect(() => {
    // Bail out of effect here if this isnt async mode or if async api call is running
    if (!props.asyncMode || props.isLoadingAsync) {
      return;
    }

    const normalizedValue = normalizeSelectedValue(props.value, props.options, getOptionValue);
    const getMenuOptions = buildMenuOptions(props.options, props.fixedOptions, normalizedValue, debouncedInputValue || '');
    (getMenuOptions.length === 1) && setFocusedOption({...getMenuOptions[0].data, index: 0 });
    setMenuOptions(getMenuOptions);
    setSelectedValue(normalizedValue);
  }, [props.value, props.options, debouncedInputValue, props.isLoadingAsync, props.asyncMode, getOptionValue]);
  */

  useEffect(() => {
    if (menuOpen) {
      props.onMenuOpen && props.onMenuOpen();
      scrollMenuIntoViewOnOpen(menuRef.current);
    } else if (!menuOpen) {
      setInputValue('');
      props.onMenuClose && props.onMenuClose();
    }
  }, [menuOpen]);

  const focusInput = () => {
    if (inputRef) {
      inputRef.focus();
    }
  };

  const onControlMouseDown = (e) => {
    if (!isFocused) {
      focusInput();
    } 
    
    const targetIsNotInput = (e.target.tagName !== 'INPUT');
    if (!menuOpen && !isAsyncDataInitializing()) {
      openMenuAndFocusOption('first');
    } else if (targetIsNotInput) {
      toggleMenu(false);
    }

    if (targetIsNotInput) {
      e.preventDefault();
    }
  };

  const isAsyncDataInitializing = () => {
    return props.isLoadingAsync && !menuOpen;
  };

  const isAsyncModeAndNotAllDataLoaded = () => {
    return props.asyncMode && !props.isAllAsyncDataLoaded;
  };

  const openMenuAndFocusOption = (focusMenuOption = 'first') => {
    if (!isArrayWithLength(menuOptions)) {
      return;
    }
    
    const selectedIndex = selectedValue ? menuOptions.findIndex((option) => option.isSelected) : -1;  
    const openAtIndex = (selectedIndex > -1) ? selectedIndex : ((focusMenuOption === 'first') ? 0 : menuOptions.length - 1);
    toggleMenu(true);
    setFocusedOption({
      ...menuOptions[openAtIndex].data, 
      index: openAtIndex
    });
  };

  const isOptionSelected = (optionValue, selectedValueKey) => {
    return selectedValueKey && (optionValue === selectedValueKey);
  };

  const keyDownHandler = (e) => {
    if (e.defaultPrevented || isAsyncDataInitializing()) {
      return;
    }

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        if (menuOpen) {
          focusOption('down');
        } else {
          openMenuAndFocusOption('first');
        }
        break;
      case 'Up':
      case 'ArrowUp':
        if (menuOpen) {
          focusOption('up');
        } else {
          openMenuAndFocusOption('last');
        }
        break;
      case 'Enter':
        if (focusedOption) {
          selectOptionFromFocused(focusedOption);
        }
        break;
      case 'Tab':        
        if (e.shiftKey 
          || !menuOpen 
          || !focusedOption 
          || (props.openMenuOnFocus 
            && (isOptionSelected(getOptionValue(focusedOption), selectedValue)) ||
                  (isOptionSelected(getFixedOptionValue(focusedOption), selectedValue)))) {
          return;
        } else if (props.tabSelectsOption && focusedOption) {
          selectOptionFromFocused(focusedOption);
        } else {
          toggleMenu(false);
        }
        break;
      case 'Esc':
      case 'Escape':
        if (menuOpen) {
          setInputValue('');
          toggleMenu(false);
        }
        break;
      default:
        return;
    }

    e.preventDefault();
  };

  const focusOption = (direction = 'first') => {
    if (!isArrayWithLength(menuOptions)) {
      return;
    }

    let nextFocus = 0;
    const focusedIndex = getFocusOptionIndex(focusedOption);
    const canNavigateEndOfMenu = !isAsyncModeAndNotAllDataLoaded();

    switch (direction) {
      case 'up':
        nextFocus = (focusedIndex > 0) 
          ? focusedIndex - 1 
          : canNavigateEndOfMenu ? (menuOptions.length - 1) : focusedIndex;
        break;
      case 'down':
        nextFocus = (focusedIndex + 1) % menuOptions.length;
        break;
      case 'last':
        nextFocus = canNavigateEndOfMenu ? (menuOptions.length - 1) : focusedIndex;
        break;
      default:
        nextFocus = 0;
        break;
    }

    const nextFocusedOption = menuOptions[nextFocus];
    if (nextFocusedOption) {
      setFocusedOption({ 
        ...nextFocusedOption.data, 
        index: nextFocus 
      });
    }
  };

  // Determines wether to use react-window (when windowMode or asyncMode or options is over 100)
  const isReactWindowEnabled = () => {
    return !!props.windowMode || !!props.asyncMode || (props.options.length > props.windowThreshold);
  };

  // Remove index prop from focusedOption object since we add that for tracking
  const selectOptionFromFocused = (focusObject) => {
    const { index, ...rest } = focusObject;
    selectOption(rest);
  };

  const selectOption = (newValue) => {
    toggleMenu(false);
    props.onChange(newValue);
  };
  
  const onMenuMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    focusInput();
    setCancelBlur(true);
  };

  const onMenuMouseUp = (e) => {
    setCancelBlur(false);
  }

  // Only applies with async mode - determines where user is with scroll position
  /*
  const onWindowedItemsRendered = useCallback(({ visibleStopIndex }) => {
    if ((typeof props.onLoadMoreOptions === 'function') && !props.isLoadingAsync) {
      const indexOfLastOption = isArrayWithLength(menuOptions) ? menuOptions.length - 1 : -1;
      if (indexOfLastOption > -1) {
        const loadMoreOptions = (indexOfLastOption - visibleStopIndex) <= props.itemsFromEndThreshold;
        props.onLoadMoreOptions(loadMoreOptions)
      }
    }  
  }, [menuOptions, props.itemsFromEndThreshold, props.isLoadingAsync]);
  */

  const onInputFocus = (e) => {
    props.onFocus && props.onFocus(e);
    setIsFocused(true);
    if (props.openMenuOnFocus) {
      openMenuAndFocusOption('first');
    }
  };

  const onInputBlur = (e) => {
    props.onBlur && props.onBlur(e);
    if (!cancelBlur) {
      setInputValue('');      
      toggleMenu(false);
      setIsFocused(false);    
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.currentTarget.value || '');
    if (!menuOpen) {
      toggleMenu(true);
    }
  };

  // By default we use the label/value keys of an option, but in the event the object is more complex or these dont exist...
  // ...an array of keys to use for filtering will need to be passed in
  // ...props.filterKeys - this can be a string for single key or an array of strings for multiple keys
  const { filterKeys } = props;
  const useCustomFilterKeys = isArrayWithLength(filterKeys);

  const stringifyOptionForFilter = (option) => {
    if (useCustomFilterKeys) {
      let stringifiedOption = '';
      for (let i = 0; i < filterKeys.length; i++) {
        stringifiedOption += `${option.data[filterKeys[i]]} `;
      }
      return stringifiedOption;
    }
    return `${option.label} ${option.value}`;
  };

  const isOptionInFilterCritera = (option, searchInputValue) => {
    const cleanOption = trimAndFormatStr(stringifyOptionForFilter(option));
    return cleanOption.indexOf(searchInputValue) > -1;
  };

  const buildMenuOptions = (options, fixedOptions, selectValue, searchInputValue) => {
    const cleanSearchInputValue = trimAndFormatStr(searchInputValue);

    const optionParser = (fixed) => (option) => {
      const label = fixed ? getFixedOptionLabel(option) : getOptionLabel(option);
      const value = fixed ? getFixedOptionValue(option) : getOptionValue(option);

      // Filter options here based on the searchInputValue (do not return parsedOption)
      // ...may want to do filtering on the optionValue to make sure no duplicates (Async parent does before loading from buffer)
      if (!fixed && cleanSearchInputValue && !isOptionInFilterCritera({ label, value, data: option }, cleanSearchInputValue)) {
        return;
      }

      const selectedValueKey = selectValue && isObject(selectValue)
        ? fixed ? getFixedOptionValue(selectValue) : getOptionValue(selectValue)
        : null;
      const key = !fixed && props.optionKey
        ? option[props.optionKey].toString() : value.toString();

      const isThisOtionSelected = () => {
        if (!selectValue) {
          return false;
        }

        //if optionsKey is set, we want to drive selection based on the value of that key matching the selected value
        if(selectValue && isObject(selectValue) && props.optionKey !== undefined) {
          return option[props.optionKey] === selectValue[props.optionKey];
        } 

        //Existing function call (does a value compare)
        return isOptionSelected(value, selectedValueKey);
      }

      return {
        fixed,
        key,
        innerProps: {
          onClick: () => selectOption(option),
        },
        data: option,
        label: label,
        value: value,
        isSelected: isThisOtionSelected(),
      };
    };
    const parseOption = optionParser(false);
    const parseFixedOption = optionParser(true);

    const menuOptionsMapReducer = (mapFn) => (acc, item) => {
      const option = mapFn(item);
      return option ? (acc.push(option), acc) : acc;
    }

    const fixedMenuOptions = isUsingFixedOptions ? fixedOptions.reduce(menuOptionsMapReducer(parseFixedOption), []) : [];
    return fixedMenuOptions.concat(options.reduce(menuOptionsMapReducer(parseOption), []));
  };


  const menuListElement = menuOpen && (
    isReactWindowEnabled()
      ? <WindowedMenu 
        menuOptions={menuOptions}
        focusedOptionIndex={focusedOptionIndex}
        focusedOptionKey={focusedOptionKey}
        formatOptionLabel={formatOptionLabel}
        itemSize={props.windowItemSize}
        maxHeight={props.windowMaxHeight}
        noOptionsMessage={props.noOptionsMessage}
        overscanCount={props.windowOverscanCount}
      />
      : <Menu
        menuRef={menuRef}
        menuOptions={menuOptions}
        focusedOptionRef={focusedOptionRef}
        focusedOptionKey={focusedOptionKey}
        formatOptionLabel={formatOptionLabel}
        setFocusedOptionRef={setFocusedOptionRef}
        noOptionsMessage={props.noOptionsMessage}
      />
  );

  return (
    <StyledDropdownWrapper 
      onKeyDown={keyDownHandler}
      className={parentNodeClasses.dropdown} 
    >
      <StyledControlWrapper 
        disabled={props.disabled} 
        invalid={props.invalid} 
        isFocused={isFocused} 
        onMouseDown={onControlMouseDown} 
        onTouchEnd={onControlMouseDown}
        className={parentNodeClasses.control}
      >
        <StyledValueWrapper data-test={props.dataTestId && `${props.dataTestId}-dropdown-value`}>
          <Value
            value={inputValue}
            selectedValue={selectedValue}
            placeholder={props.placeholder}
            placeholderColor={props.placeholderColor}
            formatOptionLabel={formatOptionLabel}
          />
          <Input 
            inputRef={handleSetInputRef}
            id={props.inputId}
            value={inputValue}
            onBlur={onInputBlur}
            onFocus={onInputFocus}
            onChange={handleInputChange}
            disabled={props.disabled}
            dataTestId={props.dataTestId}
          />
        </StyledValueWrapper>
        {(props.isLoadingAsync && menuOpen) && <AsyncLoader />}
        <ToggleIcon menuOpen={menuOpen} />      
      </StyledControlWrapper>
      {(props.invalid && props.invalidMsg && !menuOpen) && <StyledFormFeedback>{props.invalidMsg}</StyledFormFeedback>}
      <StyledMenuWrapper 
        hideMenu={!menuOpen} 
        onMouseDown={onMenuMouseDown}
        onMouseUp={onMenuMouseUp}
        className={parentNodeClasses.menu}
      >
        <StyledMenuOptionWrapper ref={menuRef}>
          {menuListElement}
        </StyledMenuOptionWrapper>
      </StyledMenuWrapper>
    </StyledDropdownWrapper>
  );
};

Dropdown.propTypes = {
  windowMode: PropTypes.bool, // Set to override the windowThreshold prop and use react-window no matter what
  windowThreshold: PropTypes.number, // option length before windowing mode takes effect
  windowItemSize: PropTypes.number, // height in px of each row in menu
  windowOverscanCount: PropTypes.number, // amount of options to render beyond scope of window (prevents any fraction of second of dealy that may happen when scrolling)
  windowMaxHeight: PropTypes.number, // Max height of the menu wrapper for window list
  optionKey: PropTypes.string,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  getFixedOptionLabel: PropTypes.func,
  getFixedOptionValue: PropTypes.func,
  formatOptionLabel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onLoadMoreOptions: PropTypes.func, // useState from async and if set true loads additional options to buffer
  setAsyncSearchOptions: PropTypes.func, // useState from async and if set true loads fresh options
  onBlur: PropTypes.func,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  options: PropTypes.array,
  fixedOptions: PropTypes.array,
  inputId: PropTypes.string,
  disabled: PropTypes.bool,
  filterKeys: PropTypes.array, // Pass array of properties on object that is an item in the array to limit search filter parsing (performance)
  startFocused: PropTypes.bool,
  startOpen: PropTypes.bool,
  searchable: PropTypes.bool,
  openMenuOnFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  placeholderColor: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  invalidMsg: PropTypes.string,
  invalid: PropTypes.bool,
  asyncMode: PropTypes.bool,
  isLoadingAsync: PropTypes.bool,
  tabSelectsOption: PropTypes.bool, // If true, when the menu is open and there is a focused option and user hits tab - that selects the option (default: true)
  searchDelay: PropTypes.number,
  isAllAsyncDataLoaded: PropTypes.bool, // Will be true once all data from api server is loaded to the buffer
  itemsFromEndThreshold: PropTypes.number,
  dataTestId: PropTypes.string, // unique id to add to data-test attributes (example: passing user - would then become data-test="user-dropdown-value")
};

Dropdown.defaultProps = {
  searchDelay: 0,
  windowMode: false,
  windowItemSize: 35,
  windowOverscanCount: 1,
  windowMaxHeight: 300,
  windowThreshold: 100,
  startFocused: false,
  asyncMode: false,
  startOpen: false,
  openMenuOnFocus: false,
  searchable: true,
  tabSelectsOption: true,
  defaultValue: null,
  placeholder: 'Select option..',
  placeholderColor: '#B5BBC1',
  noOptionsMessage: 'No options',
};

export {
  Dropdown
};